/**
 * Backwards-compatible re-export of the Firebase-backed auth module.
 *
 * The initial Lovable scaffold used a tiny stub context at this path with a
 * single `isSubscriber` toggle. That stub has been replaced by a full Firebase
 * Auth integration in `@/lib/firebase/auth`. This file preserves the original
 * import path (`@/lib/auth`) so existing components keep compiling.
 *
 * Additionally, this module wraps `useAuth()` with a developer-state override
 * (see `@/lib/dev-state`) so we can preview different signed-in / subscribed
 * states from a switcher in the header without touching the real backend.
 * When the dev state is "real", the underlying Firebase auth value is returned
 * unchanged.
 */

import {
  AuthProvider as FirebaseAuthProvider,
  useAuth as useFirebaseAuth,
  useAuthActions as useFirebaseAuthActions,
  type AuthContextValue,
} from "./firebase/auth";
import { DevStateProvider, useDevState } from "./dev-state";
import { isLocalDevHost } from "./dev-mode";
import { resolveDevCreators } from "./membership-tier";
import type { ReactNode } from "react";

export type {
  AuthContextValue,
  AuthActionResult,
  SignInInput,
  SignUpInput,
} from "./firebase/auth";

export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <DevStateProvider>
      <FirebaseAuthProvider>{children}</FirebaseAuthProvider>
    </DevStateProvider>
  );
}

/**
 * Returns the Firebase auth value, with developer-state overrides applied
 * when the dev switcher is set to anything other than "real" on localhost.
 */
export function useAuth(): AuthContextValue {
  const real = useFirebaseAuth();
  const dev = useDevState();

  if (dev.state === "real" || !isLocalDevHost()) return real;

  // Override: simulate signed-in / subscribed state without mutating Firebase.
  const signedIn = dev.isSignedIn;
  const isSubscriber = dev.isSubscribed;
  const creators: "all" | string[] = dev.isSubscribed
    ? resolveDevCreators(dev.tier)
    : [];
  const canAccessCreator = (id: string) => {
    if (!dev.isSubscribed) return false;
    if (creators === "all") return true;
    return creators.includes(id);
  };
  // When the dev state simulates a subscription, also expose the simulated
  // membershipType ('legacy' or 'v2') so gating UI (e.g. the library page's
  // full-vault-vs-weekly-window split) can be previewed without touching
  // Firestore. When the dev state is "logged out" or "logged in, not
  // subscribed", `membershipType` collapses back to undefined.
  const membershipType = dev.membershipType;
  const isLegacyMember = signedIn && membershipType === "legacy";

  return {
    ...real,
    signedIn,
    isSubscriber,
    creators,
    canAccessCreator,
    membershipType,
    isLegacyMember,
  };
}

export const useAuthActions = useFirebaseAuthActions;
